import React, { useEffect, useState } from "react";

import { startOfYear } from "date-fns";
import Decimal from "decimal.js";
import { useHistory, useParams } from "react-router-dom";

import useGetTaxBudgets from "@fartherfinance/frontend/api/Investment/hooks/useGetTaxBudgets";
import usePostHypotheticalProposalV2 from "@fartherfinance/frontend/api/Investment/hooks/usePostHypotheticalProposalV2";
import { ClientId } from "@fartherfinance/frontend/api/Types";

import HypotheticalFlowBody from "../../Client/TaxBudgets/FlowBody/HypotheticalFlowBody";
import BaseLayout from "../../Layouts/BaseLayout/BaseLayout";
import { useCreateEditTaxBudgetContext } from "../reducer/Context";
import AdvisorHypotheticalFlowBody from "@src/multiCustodian/components/Advisor/TaxBudgets/FlowBody/HypotheticalFlowBody";
import FlowInit from "@src/multiCustodian/components/TaxBudgets/FlowInit/FlowInit";
import { actions } from "@src/multiCustodian/components/TaxBudgets/reducer/actions";
import { HypotheticalTradesTable } from "@src/multiCustodian/components/TaxBudgets/reducer/types";
import SecondaryTitle from "@src/multiCustodian/components/TaxBudgets/SecondaryTitle/SecondaryTitle";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import useRequestAuth from "@src/multiCustodian/hooks/useRequestAuth";
import useStatusNotification from "@src/multiCustodian/hooks/useStatusNotification";
import { trackEvent } from "@src/multiCustodian/services/tracking";
import FlowHero from "@src/sharedComponents/FlowHero/FlowHero";
import LogoLoadingStill from "@src/sharedComponents/LogoLoadingStill/LogoLoadingStill";
import { handleApiError } from "@src/utils/handleApiError";

import {
  createTradesTable,
  retrieveHypotheticalAssignments,
  transformHypotheticalProposal,
  ytdNetGainsRealized,
} from "./helpers";

const CreateHypothetical: React.FC = () => {
  const { clientId } = useParams<{
    clientId: ClientId;
  }>();

  const { state, dispatch } = useCreateEditTaxBudgetContext();

  const statusNotification = useStatusNotification();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const {
    isAdvisor,
    buttonText,
    isContinueDisabled,
    pageIndex,
    pageTitle,
    data,
  } = state;

  const history = useHistory();

  const advisorAuth = useAdvisorRequestAuth();

  const advisorWithClientAuth =
    advisorAuth !== null ? { ...advisorAuth, clientId: clientId } : null;

  const clientAuth = useRequestAuth();

  const auth = advisorWithClientAuth ?? clientAuth;

  const { data: budgetsData, isLoading: isLoadingBudgets } = useGetTaxBudgets(
    { clientId: [clientId], isActive: true },
    auth
  );

  const budgets = budgetsData?.data ?? [];

  const postHypotheticalTrades = usePostHypotheticalProposalV2(auth);

  useEffect(() => {
    setIsLoading(isLoadingBudgets);
  }, [isLoadingBudgets]);

  const finish = () => {
    dispatch({ type: actions.RESET_TO_DEFAULT_DATA, payload: undefined });

    const pathname = isAdvisor
      ? `/Advisor/Clients/${clientId}/TaxBudgets`
      : `/Client/${clientId}/TaxBudgets`;

    history.push({
      ...history.location,
      pathname,
    });
  };

  const handleOnClickBack = () => {
    switch (pageIndex) {
      case 0:
        finish();
        break;
      default:
        dispatch({ type: actions.DECREMENT_PAGE_INDEX, payload: undefined });
        break;
    }
  };

  const postHypotheticalProposalV2 = async () => {
    if (auth === null) {
      throw new Error("auth is not defined");
    }

    try {
      setIsLoading(true);

      // for each assignment, get the portfolio plan and then get the entries
      const portfolioAssignmentsWithEntries =
        await retrieveHypotheticalAssignments(
          data.hypotheticalTradeGroups,
          auth
        );

      const response = await postHypotheticalTrades({
        clientId: clientId,
        portfolioPlanAssignments: portfolioAssignmentsWithEntries,
        taxBudget: {
          budgetAmount: new Decimal(data.budgetAmount),
          excludedVirtualAccountIds: data.excludedVirtualAccountIds,
          clientIds: [clientId],
          year: startOfYear(new Date()),
        },
      });

      trackEvent({
        name: isAdvisor
          ? "Advisor Run Hypothetical-Tax-Budget"
          : "Client Run Hypothetical-Tax-Budget",
      });

      const hypotheticalProposalWithTaxBudgets = transformHypotheticalProposal(
        response.proposalsWithTaxBudget.proposals
      );

      const hypotheticalProposalWithoutTaxBudgets =
        transformHypotheticalProposal(
          response.proposalsWithoutTaxBudget.proposals
        );

      const ytdNetGainWithTaxBudget = ytdNetGainsRealized(
        response.proposalsWithTaxBudget.proposals
      );

      const ytdNetGainWithoutTaxBudget = ytdNetGainsRealized(
        response.proposalsWithoutTaxBudget.proposals
      );

      const tradesTable =
        data.hypotheticalTradeGroups.reduce<HypotheticalTradesTable>(
          (acc, tradingGroup) => {
            const tradeProposalForTG = createTradesTable(
              response,
              tradingGroup
            );
            acc[tradingGroup.tradingGroupId] = tradeProposalForTG;

            return acc;
          },
          {}
        );

      statusNotification(
        "Success creating hypothetical trade proposal",
        "Success"
      );

      dispatch({
        type: actions.SET_HYPOTHETICAL_PROPOSAL_V2,
        payload: {
          hypotheticalTradesTable: tradesTable,
          proposalsWithTaxBudget: hypotheticalProposalWithTaxBudgets,
          proposalsWithoutTaxBudget: hypotheticalProposalWithoutTaxBudgets,
          ytdNetGainWithoutTaxBudget,
          ytdNetGainWithTaxBudget,
        },
      });

      dispatch({ type: actions.INCREMENT_PAGE_INDEX, payload: undefined });
    } catch (error) {
      const errorMessage = "Hypothetical Proposal error:";
      const genericErrorMessage = "Something went wrong. Please try again.";

      handleApiError({
        error,
        errorMessage,
        genericErrorMessage,
        notify: statusNotification,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleOnClickContinue = () => {
    switch (pageIndex) {
      case 3:
        postHypotheticalProposalV2();
        break;
      case 4:
        finish();
        break;
      default:
        dispatch({ type: actions.INCREMENT_PAGE_INDEX, payload: undefined });
        break;
    }
  };

  const handleOnClickCancel = () => finish();

  return (
    <BaseLayout showSideNav={false}>
      <FlowInit budgets={budgets || []}>
        <FlowHero
          onClickBack={handleOnClickBack}
          onClickCancel={handleOnClickCancel}
          onClickContinue={handleOnClickContinue}
          primaryButtonText={buttonText}
          primaryButtonIsDisabled={isContinueDisabled}
          secondaryTitle={<SecondaryTitle title={"Hypothetical Scenario"} />}
          title={pageTitle}
        />

        {isLoading && <LogoLoadingStill onTop noBackgroundColor />}

        {!isLoading && isAdvisor && <AdvisorHypotheticalFlowBody />}
        {!isLoading && !isAdvisor && <HypotheticalFlowBody />}
      </FlowInit>
    </BaseLayout>
  );
};

export default CreateHypothetical;
